import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CalendarSync, CheckCircle2, Loader2, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { probetrainingAPI } from '@/lib/api';
import SmsDialog from '@/components/SmsDialog';

/* Probetraining nachbearbeiten: Termin verschieben (Betreiber 06.10.2026).
   Umgebucht wird über den Termin-Kern: erst der neue Platz, dann wird der alte
   frei; Lead und Liste ziehen mit. Die Terminliste kommt vom Server, weil dort
   die Regeln stehen (die eigene Woche zählt nicht gegen das Wochenkontingent,
   andere volle Wochen sieht nur, wer sie überstimmen darf).
   Nach dem Verschieben bleibt der Dialog stehen und sagt, ob eine Bestätigung
   mit der neuen Zeit bereitliegt. Ein Toast wäre dafür zu schnell weg. */
const NACHRICHT = {
  freigabe: { ton: 'ok', text: 'Die Bestätigung mit der neuen Zeit liegt in der Freigabe-Zentrale.' },
  automatik: { ton: 'ok', text: 'Die Bestätigung mit der neuen Zeit geht in Kürze automatisch raus. Hat die Automatik Bedenken, bleibt sie in der Freigabe-Zentrale liegen.' },
  fehlt: { ton: 'achtung', text: 'Es geht keine Nachricht automatisch raus. Bitte gib dem Kunden die neue Zeit selbst durch.' },
};

export default function ProbetrainingVerschieben({ buchungId, onClose, onFertig }) {
  const [info, setInfo] = useState(null);
  const [gesperrt, setGesperrt] = useState('');       // Grund, warum sich dieser Termin nicht verschieben lässt
  const [kursSlug, setKursSlug] = useState('');
  const [wunsch, setWunsch] = useState('');
  const [slots, setSlots] = useState(null);
  const [terminId, setTerminId] = useState('');
  const [grund, setGrund] = useState('');
  const [begruendung, setBegruendung] = useState('');
  const [busy, setBusy] = useState(false);
  const [ergebnis, setErgebnis] = useState(null);
  const [neuLaden, setNeuLaden] = useState(0);
  const [smsOffen, setSmsOffen] = useState(false);        // nach dem Verschieben: per SMS Bescheid geben
  const [ladefehler, setLadefehler] = useState(false);  // Terminliste nicht ladbar — nicht dasselbe wie „keine Termine“

  useEffect(() => {
    if (!buchungId) return undefined;
    let aktiv = true;
    setInfo(null); setGesperrt(''); setKursSlug(''); setWunsch(''); setSlots(null); setTerminId('');
    setGrund(''); setBegruendung(''); setErgebnis(null); setBusy(false); setLadefehler(false); setSmsOffen(false);
    probetrainingAPI.verschiebenInfo(buchungId)
      .then(r => { if (!aktiv) return; setInfo(r.data); setKursSlug(r.data.kurs_slug || ''); })
      .catch(e => { if (aktiv) setGesperrt(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Der Termin konnte nicht geladen werden.'); });
    return () => { aktiv = false; };
  }, [buchungId]);

  useEffect(() => {
    if (!buchungId || !info || !kursSlug || ergebnis) return undefined;
    let aktiv = true;
    setSlots(null); setLadefehler(false);
    probetrainingAPI.verschiebenSlots(buchungId, { kurs_slug: kursSlug, wunsch_datum: wunsch || undefined })
      .then(r => {
        if (!aktiv) return;
        const s = r.data || [];
        setSlots(s);
        // Nichts vorauswählen außer dem ausdrücklich gewünschten Tag: Verschieben ist kein Vorschlag.
        // Eine bestehende Auswahl bleibt nur beim Neuladen nach einem 409 (Kurs- und Tageswechsel leeren sie).
        setTerminId(alt => (s.some(x => x.termin_id === alt) ? alt : (s.find(x => x.am_wunschtag && !x.kontingent_voll)?.termin_id || '')));
      })
      .catch(e => {
        if (!aktiv) return;
        const d = e?.response?.data?.detail;
        // 409/404: Die Buchung selbst lässt sich nicht mehr verschieben (jemand war schneller) — den Grund zeigen
        if ([404, 409].includes(e?.response?.status) && typeof d === 'string') { setGesperrt(d); return; }
        setSlots([]); setLadefehler(true);
      });
    return () => { aktiv = false; };
  }, [buchungId, info, kursSlug, wunsch, neuLaden]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!buchungId) return null;
  const gewaehlt = slots?.find(x => x.termin_id === terminId);
  const kontingentVoll = !!gewaehlt?.kontingent_voll;

  const verschieben = async () => {
    if (!terminId) { toast.error('Bitte einen neuen Termin wählen'); return; }
    if (kontingentVoll && !begruendung.trim()) { toast.error('Diese Woche ist voll. Bitte begründen, warum trotzdem verschoben wird'); return; }
    setBusy(true);
    try {
      const r = await probetrainingAPI.verschieben(buchungId, {
        neuer_termin_id: terminId, grund: grund.trim(), begruendung_kontingent: kontingentVoll ? begruendung.trim() : '' });
      setErgebnis(r.data);
      onFertig?.(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Verschieben fehlgeschlagen');
      // 409 heißt meist: Die Liste ist veraltet (Termin inzwischen voll, Woche voll). Frisch laden.
      if (e?.response?.status === 409) setNeuLaden(n => n + 1);
    }
    setBusy(false);
  };

  const nachricht = ergebnis ? (NACHRICHT[ergebnis.bestaetigung] || NACHRICHT.fehlt) : null;

  return (
    <Dialog open onOpenChange={o => { if (!o && !busy) { if (gesperrt) onFertig?.(null); onClose(); } }}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Termin verschieben</DialogTitle></DialogHeader>
        {/* Ein Erfolg geht vor: Der Sperrtext einer Nachlade-Anfrage darf die Rückmeldung nicht verdecken */}
        {ergebnis ? (
          <div className="space-y-4 text-sm" data-testid="pv-ergebnis">
            <div className="rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 p-3 space-y-1">
              <p className="font-semibold text-emerald-800 dark:text-emerald-300 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Verschoben</p>
              <p className="text-muted-foreground line-through">{ergebnis.vorher?.label}</p>
              <p className="font-medium">{ergebnis.termin?.label}</p>
            </div>
            <p className={nachricht.ton === 'achtung'
              ? 'rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-3 font-medium text-amber-900 dark:text-amber-200'
              : 'rounded-md border border-border bg-muted/40 p-3'} data-testid={`pv-nachricht-${ergebnis.bestaetigung}`}>
              {nachricht.text}
            </p>
            <div className="flex flex-wrap justify-end gap-2">
              {ergebnis.lead_id && (
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setSmsOffen(true)} data-testid="pv-sms"><Smartphone className="w-4 h-4" /> Per SMS Bescheid geben</Button>
              )}
              <Button size="sm" onClick={onClose} data-testid="pv-fertig">Fertig</Button>
            </div>
            {/* Als Kind dieses Dialogs eingehängt: Zwei Dialoge nebeneinander streiten sich um Fokus und Klicks */}
            <SmsDialog leadId={smsOffen ? ergebnis.lead_id : null} buchungId={ergebnis.buchung_id} anlass="verlegt"
                       onClose={() => setSmsOffen(false)} onFertig={() => onFertig?.(ergebnis)} />
          </div>
        ) : gesperrt ? (
          <div className="space-y-4 text-sm" data-testid="pv-gesperrt">
            <p className="rounded-md border border-border bg-muted/40 p-3">{gesperrt}</p>
            <div className="flex justify-end"><Button variant="outline" size="sm" onClick={() => { onFertig?.(null); onClose(); }}>Schließen</Button></div>
          </div>
        ) : !info ? (
          <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Termin wird geladen…</p>
        ) : (
          <div className="space-y-4 text-sm">
            <div className="rounded-md border border-border bg-muted/40 p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Bisher</p>
              <p className="font-medium" data-testid="pv-bisher">{info.label}</p>
              {info.teilnehmer && <p className="text-xs text-muted-foreground">{info.teilnehmer}</p>}
            </div>
            <div className="grid grid-cols-[1fr_auto] gap-3">
              <label className="space-y-1 text-xs"><span className="font-semibold uppercase tracking-wider text-muted-foreground">Kurs</span>
                <select value={kursSlug} disabled={busy} onChange={e => { setKursSlug(e.target.value); setTerminId(''); }} className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="pv-kurs">
                  {(info.kurse || []).map(k => <option key={k.slug} value={k.slug}>{k.name}</option>)}
                </select>
              </label>
              <label className="space-y-1 text-xs"><span className="font-semibold uppercase tracking-wider text-muted-foreground">Wunschtag</span>
                <Input type="date" value={wunsch} disabled={busy} onChange={e => { setWunsch(e.target.value); setTerminId(''); }} className="h-9" data-testid="pv-datum" />
              </label>
            </div>
            <div className="space-y-1.5" data-testid="pv-slots">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Neuer Termin</p>
              {slots === null ? <p className="text-xs text-muted-foreground">Termine werden geladen…</p>
                : ladefehler ? (
                  <div className="text-sm border border-dashed border-border rounded-md p-3 flex items-center justify-between gap-3" data-testid="pv-ladefehler">
                    <span className="text-muted-foreground">Die Termine konnten nicht geladen werden.</span>
                    <Button variant="outline" size="sm" onClick={() => setNeuLaden(n => n + 1)}>Erneut laden</Button>
                  </div>
                )
                : slots.length === 0 ? <p className="text-sm text-muted-foreground border border-dashed border-border rounded-md p-3">Keine freien Termine für diesen Kurs in den nächsten sechs Wochen. Bitte einen anderen Kurs wählen.</p>
                : slots.map(s => (
                  <label key={s.termin_id} className={`flex items-center gap-3 rounded-md border px-3 py-2 cursor-pointer text-sm ${terminId === s.termin_id ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'}`}>
                    <input type="radio" name="pv-termin" checked={terminId === s.termin_id} onChange={() => setTerminId(s.termin_id)} data-testid={`pv-slot-${s.termin_id}`} />
                    <span className="flex-1">{s.label}</span>
                    {s.am_wunschtag && <Badge variant="secondary" className="text-[10px]">Wunschtag</Badge>}
                    {s.kontingent_voll && <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700 dark:text-amber-300">Wochenkontingent voll</Badge>}
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{s.restplaetze} frei</span>
                  </label>
                ))}
              {!!slots?.length && <p className="text-xs text-muted-foreground">Später gewünscht? Wähle oben den Wunschtag, dann stehen die Termine um diesen Tag zuerst.</p>}
            </div>
            {kontingentVoll && (
              <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-3 space-y-2 text-xs" data-testid="pv-kontingent">
                <p className="font-bold text-amber-800 dark:text-amber-300">In dieser Woche sind die Probetrainings für diesen Kurs schon vergeben.</p>
                <p className="text-muted-foreground">Als Inhaber oder Manager kannst du trotzdem dorthin verschieben. Die Begründung steht an der Buchung und als Notiz am Lead.</p>
                <Input value={begruendung} onChange={e => setBegruendung(e.target.value)} placeholder="Begründung (Pflicht)" className="h-9" maxLength={300} data-testid="pv-begruendung-kontingent" />
              </div>
            )}
            <Input value={grund} onChange={e => setGrund(e.target.value)} placeholder="Grund (optional, steht als Notiz am Lead)" className="h-9" maxLength={300} data-testid="pv-grund" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>Abbrechen</Button>
              <Button size="sm" onClick={verschieben} disabled={busy || !terminId || slots === null} className="gap-1.5" data-testid="pv-verschieben">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarSync className="w-4 h-4" />} Termin verschieben</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
